import axios from "axios";
import { useState } from "react";
import Form from "react-bootstrap/Form";
import Timer from "../../../components/Timer";
import { useDataContext } from "../../../utils/DataContext";
import { buttonText } from "../../../utils/buttonText";

function AnomalySynthesisPage(props) {
  const { item, loadDatasetList } = props;
  const { userToken } = useDataContext();

  const [itemName, setItemName] = useState("");
  const [anomalyType, setAnomalyType] = useState("");
  const [maxIterations, setMaxIterations] = useState(3);
  const [qualityThreshold, setQualityThreshold] = useState(4.0);
  const [num, setNum] = useState(1);
  const [skipEvaluation, setSkipEvaluation] = useState(true);
  const [keepMask, setKeepMask] = useState(false);
  const [newDatasetName, setNewDatasetName] = useState("");
  const [loading, setLoading] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);

  const resolveTargetDatasetName = () =>
    newDatasetName.trim() || item.name;

  const submitSynthesis = async (targetName, appendToExisting = false) => {
    return axios.post("/datasets/anomaly_synthesis", {
      source_dataset_name: item.name,
      new_dataset_name: targetName,
      item_name: itemName.trim(),
      anomaly_type: anomalyType.trim(),
      max_iterations: skipEvaluation ? 1 : Number(maxIterations),
      quality_threshold: Number(qualityThreshold),
      num: Math.max(1, Number(num)),
      skip_evaluation: skipEvaluation,
      keep_mask: keepMask,
      append_to_existing: appendToExisting,
      requesterToken: userToken,
    });
  };

  const handleSkipEvaluationChange = (next) => {
    setSkipEvaluation(next);
    if (next) {
      setMaxIterations(1);
    }
  };

  const runAnomalySynthesis = async () => {
    const targetName = resolveTargetDatasetName();
    setLoading(true);
    setPreviewImages([]);
    try {
      let response;
      // empty name defaults to current dataset → append in place
      if (targetName === item.name) {
        response = await submitSynthesis(targetName, true);
      } else {
        try {
          response = await submitSynthesis(targetName, false);
        } catch (error) {
          const data = error.response?.data;
          if (error.response?.status === 409 && data?.code === "dataset_exists") {
            const existingName = data.new_dataset_name || targetName;
            const append = window.confirm(
              `Dataset "${existingName}" already exists.\nAppend newly generated images to this dataset?\n\nOK: Keep existing data and append\nCancel: Go back and enter a different name`
            );
            if (!append) {
              return;
            }
            response = await submitSynthesis(existingName, true);
          } else {
            throw error;
          }
        }
      }
      const images = response.data?.images || [];
      const createdName =
        response.data?.new_dataset_name || targetName;
      setPreviewImages(images.slice(0, 12));
      await loadDatasetList();
      window.alert(
        `Generated ${response.data?.generated_count || 0} image(s) in dataset "${createdName}"`
      );
    } catch (error) {
      const data = error.response?.data;
      const errorMsg =
        typeof data === "string"
          ? data
          : data?.message || data?.error || error.message;
      window.alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="anomaly-synthesis-page">
      <div className="anomaly-synthesis-main">
        <div className="anomaly-synthesis-form">
          <Form.Group className="mb-3">
            <Form.Label>Item Name</Form.Label>
            <Form.Control
              type="text"
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              placeholder="e.g. Plastic products"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Anomaly Type</Form.Label>
            <Form.Control
              type="text"
              value={anomalyType}
              onChange={(e) => setAnomalyType(e.target.value)}
              placeholder="e.g. hole, crack, scratch"
            />
          </Form.Group>
          <div className="anomaly-params-row">
            <Form.Group>
              <Form.Label>Max Iterations</Form.Label>
              <Form.Control
                type="number"
                min={1}
                max={10}
                value={skipEvaluation ? 1 : maxIterations}
                onChange={(e) => setMaxIterations(e.target.value)}
                placeholder="e.g. 1~10"
                disabled={skipEvaluation}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Quality Threshold</Form.Label>
              <Form.Control
                type="number"
                min={0}
                max={10}
                step={0.1}
                value={qualityThreshold}
                onChange={(e) => setQualityThreshold(e.target.value)}
                placeholder="e.g. 0~10"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Images per Source</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={num}
                onChange={(e) => setNum(e.target.value)}
                placeholder="e.g. 1"
              />
            </Form.Group>
          </div>
          <Form.Group className="mb-3">
            <Form.Label>New Dataset Name</Form.Label>
            <Form.Control
              type="text"
              value={newDatasetName}
              onChange={(e) => setNewDatasetName(e.target.value)}
              placeholder={`default: ${item.name}`}
            />
          </Form.Group>
          <div className="anomaly-toggle-row">
            <div>
              <p className="anomaly-toggle-title">Skip evaluation</p>
              <p className="anomaly-toggle-desc">
                Skip quality scoring and save generated images directly
              </p>
            </div>
            <button
              type="button"
              className={`anomaly-switch ${skipEvaluation ? "on" : ""}`}
              role="switch"
              aria-checked={skipEvaluation}
              onClick={() => handleSkipEvaluationChange(!skipEvaluation)}
            >
              <span className="anomaly-switch-knob" />
            </button>
          </div>
          <div className="anomaly-toggle-row">
            <div>
              <p className="anomaly-toggle-title">Keep mask</p>
              <p className="anomaly-toggle-desc">
                Save mask images with anomaly images; turn off to discard masks
              </p>
            </div>
            <button
              type="button"
              className={`anomaly-switch ${keepMask ? "on" : ""}`}
              role="switch"
              aria-checked={keepMask}
              onClick={() => setKeepMask(!keepMask)}
            >
              <span className="anomaly-switch-knob" />
            </button>
          </div>
          <button
            type="button"
            className="org-btn org-btn-primary"
            onClick={runAnomalySynthesis}
            disabled={loading}
          >
            {buttonText(loading ? (
              <span>
                <Timer loadingStates={[loading]} />
              </span>
            ) : (
              "Run Anomaly Synthesis"
            ))}
          </button>
          {previewImages.length > 0 && (
            <div className="anomaly-preview-grid">
              {previewImages.map((img, idx) => (
                <img
                  key={idx}
                  src={`data:image/png;base64,${img}`}
                  alt={`anomaly-${idx}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AnomalySynthesisPage;
